import { Box } from "@mui/material";
import Link from "next/link";
import { useRouter } from "next/router";
import { type RefObject, useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { loginHrefReturningTo } from "src/domains/shared/utils/loginReturnPath";
import { useScrollFrame } from "../siteKit/motion/scrollFrame";
import { SITE_BUTTON_STYLES } from "../siteKit/siteButtonStyles";
import { chapterNumber, type ManualChapterId } from "./manualChapters";
import {
  type ManualChapterText,
  ManualTopicBlock,
  type ManualTopicText,
} from "./ManualChapterSection";
import { jumpToManualSection, MANUAL_STEP_QUERY } from "./manualJump";
import { MANUAL_GATE_VISIBLE_ATTR, MANUAL_STYLES } from "./manualStyles";
import { MANUAL_CHAPTERS } from "./manualTopics";

/** 손님 안내 — 절 주소로 온 손님은 그 절 대신 여기로 간다 */
export const MANUAL_LOGIN_GATE_ID = "manual-login-gate";
const INQUIRY_HREF = "/inquiry";
/** 맛보기로 보이는 절: 01장 첫 절 「새 프로젝트 만들기」 */
const [PREVIEW_CHAPTER] = MANUAL_CHAPTERS;
const [PREVIEW_TOPIC] = PREVIEW_CHAPTER.topics;
/**
 * 안내 띠가 나타나는 기준선(넓은 화면) — 첫 화면 여덟 장 줄(목차)의 가운데가 화면 세로 이 비율(아래 1/4 선) 위로 올라오면
 * 보인다(사용자: 「더 빨리」 — 가운데(0.5)는 늦었다). 좁은 화면은 스타일(`gateVeil`)이 처음부터 보이게 둔다
 */
const GATE_REVEAL_VIEWPORT_RATIO = 0.75;

/** 절 주소(`#절`·`?step=`)로 왔는지 — 그 절은 문서에 없으므로 안내로 간다 */
const cameForTopic = () =>
  window.location.hash !== "" ||
  new URLSearchParams(window.location.search).has(MANUAL_STEP_QUERY);

const setGateVisible = (veil: HTMLElement, visible: boolean) => {
  if (veil.hasAttribute(MANUAL_GATE_VISIBLE_ATTR) === visible) return;
  if (visible) veil.setAttribute(MANUAL_GATE_VISIBLE_ATTR, "");
  else veil.removeAttribute(MANUAL_GATE_VISIBLE_ATTR);
};

/**
 * 로그인하지 않은 방문자의 매뉴얼 본문(service-manual 「로그인하지 않은 방문자의 매뉴얼」): 01장 왼쪽 열(번호·이름·절 이름은
 * 글자만)과 첫 절 맛보기(`inert` — 누르거나 초점을 받지 않음)에서 내용이 끝나고, 화면 아래에 붙은 전체 너비 연한 파란 띠 안의
 * 로그인 안내가 넓은 화면에서는 목차 가운데가 화면 아래 1/4 선까지 올라오면 떠올라 끝까지, 좁은 화면에서는 처음부터 보인다. 다른 절·장은
 * 그리지 않는다(DOM 에 없다).
 * 「로그인」 은 지금 주소로 돌아오는 로그인 화면, 「1:1 문의하기」 는 문의 페이지.
 */
export function ManualGuestPreview({
  chapters,
  topics,
  chapterLinesRef,
}: {
  chapters: Readonly<Record<ManualChapterId, ManualChapterText>>;
  topics: Readonly<Record<string, ManualTopicText>>;
  /** 첫 화면의 여덟 장 줄 묶음 — 띠가 나타날 때를 재는 기준 */
  chapterLinesRef: RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation("landing");
  const router = useRouter();
  const headingId = useId();
  const gateTitleId = useId();
  const veilRef = useRef<HTMLDivElement>(null);
  const { name, open } = chapters[PREVIEW_CHAPTER.id];

  useScrollFrame((_scrollY, viewportHeight) => {
    const lines = chapterLinesRef.current;
    const veil = veilRef.current;
    if (!lines || !veil) return;
    const { top, height } = lines.getBoundingClientRect();
    const linesCenter = top + height / 2;
    setGateVisible(
      veil,
      linesCenter <= viewportHeight * GATE_REVEAL_VIEWPORT_RATIO
    );
  });

  useEffect(() => {
    if (!cameForTopic()) return;
    // 절 주소로 온 손님은 안내로 바로 가므로, 스크롤 프레임을 기다리지 않고 띠를 먼저 보인다
    if (veilRef.current) setGateVisible(veilRef.current, true);
    jumpToManualSection(MANUAL_LOGIN_GATE_ID);
  }, []);

  return (
    <>
      <Box sx={MANUAL_STYLES.doc}>
        <Box
          component="section"
          aria-labelledby={headingId}
          sx={MANUAL_STYLES.chapter}
        >
          <Box
            component="aside"
            sx={[MANUAL_STYLES.side, MANUAL_STYLES.guestSide]}
          >
            <Box component="p" sx={MANUAL_STYLES.chapterNumber}>
              {chapterNumber(0)}
            </Box>
            <Box component="h2" id={headingId} sx={MANUAL_STYLES.chapterName}>
              {name}
            </Box>
            <Box
              component="ul"
              aria-label={t("site.manual.tocLabel", { chapter: name })}
              sx={MANUAL_STYLES.guestToc}
            >
              {PREVIEW_CHAPTER.topics.map(({ id }) => (
                <li key={id}>{topics[id].title}</li>
              ))}
            </Box>
          </Box>
          <Box sx={MANUAL_STYLES.body}>
            <Box inert sx={MANUAL_STYLES.guestTopic}>
              <ManualTopicBlock
                topic={PREVIEW_TOPIC}
                text={topics[PREVIEW_TOPIC.id]}
                intro={open}
                slots={{}}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box ref={veilRef} sx={MANUAL_STYLES.gateVeil}>
        <Box
          component="section"
          id={MANUAL_LOGIN_GATE_ID}
          tabIndex={-1}
          aria-labelledby={gateTitleId}
          sx={MANUAL_STYLES.gate}
        >
          <Box component="h2" id={gateTitleId} sx={MANUAL_STYLES.gateTitle}>
            {t("site.manual.gate.title")}
          </Box>
          <Box component="p" sx={MANUAL_STYLES.gateText}>
            {t("site.manual.gate.text")}
          </Box>
          <Box sx={MANUAL_STYLES.gateActions}>
            <Box
              component={Link}
              href={loginHrefReturningTo(router.asPath)}
              sx={SITE_BUTTON_STYLES.primaryLarge}
            >
              {t("site.manual.gate.login")}
            </Box>
            <Box
              component={Link}
              href={INQUIRY_HREF}
              sx={SITE_BUTTON_STYLES.outlineLarge}
            >
              {t("site.manual.gate.inquiry")}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
